import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { DIM_COLOR } from "@/components/DimensionBar";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from "recharts";
import { Plus, Download, FileText, Copy, Lock, Sparkles, Mail, Users, Upload, BellRing, CalendarClock, CalendarDays, Eye, X, Pencil, Trash2, XCircle, RotateCcw, Send, Check, Building2, ListChecks, Archive } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ReportEditor, ReportNotice, waitForDraft } from "@/components/org/ReportEditor";
import { format, parseISO } from "date-fns";

const withOrgId = (u, orgId) => (orgId ? `${u}${u.includes("?") ? "&" : "?"}org_id=${orgId}` : u);
const inp = "bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none";

function DateField({ value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex items-center gap-2 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-left focus:border-brass outline-none hover:border-brass">
          <CalendarDays className="w-4 h-4 text-brass shrink-0" />
          <span className={value ? "text-[#F3F4F6]" : "text-[#6B7280]"}>{value ? format(selected, "d MMM yyyy") : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#1A1D27] border-[var(--cg-border)] text-[#F3F4F6]" align="start">
        <Calendar mode="single" selected={selected} defaultMonth={selected}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

export default function OrgDashboard() {
  const { orgId } = useParams();
  const saMode = !!orgId;
  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [agg, setAgg] = useState(null);
  const [interp, setInterp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", cohort_name: "" });

  const loadOverview = useCallback(async () => {
    const r = await api.get(withOrgId("/org/overview", orgId));
    setOverview(r.data);
    if (r.data.campaigns.length && !selected) setSelected(r.data.campaigns[0].id);
    setLoading(false);
  }, [selected, orgId]);

  useEffect(() => { loadOverview(); }, []); // eslint-disable-line

  const loadCampaign = useCallback(async (cid) => {
    if (!cid) return;
    setAgg(null); setInterp(null);
    try {
      const r = await api.get(withOrgId(`/org/campaigns/${cid}/aggregate`, orgId));
      setAgg(r.data);
      if (saMode) {
        const ir = await api.get(withOrgId(`/org/campaigns/${cid}/interpretation`, orgId));
        setInterp(ir.data.interpretation);
        if (ir.data.job?.status === "running") {
          setGenBusy(true);
          waitForDraft(() => api.get(withOrgId(`/org/campaigns/${cid}/interpretation`, orgId)))
            .then(setInterp).catch((e) => toast.error(e.message)).finally(() => setGenBusy(false));
        }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [orgId, saMode]);

  useEffect(() => { if (selected) loadCampaign(selected); }, [selected, loadCampaign]);

  const createCampaign = async () => {
    try {
      const r = await api.post("/org/campaigns", form);
      toast.success(`Campaign created — code ${r.data.campaign.code}`);
      setShowCreate(false); setForm({ name: "", cohort_name: "" });
      await loadOverview(); setSelected(r.data.campaign.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const generate = async () => {
    setGenBusy(true);
    try {
      await api.post(withOrgId(`/org/campaigns/${selected}/interpretation`, orgId));
      toast.message("Drafting the report… this can take a minute or two");
      const done = await waitForDraft(() => api.get(withOrgId(`/org/campaigns/${selected}/interpretation`, orgId)));
      setInterp(done); toast.success("Report draft generated");
    } catch (err) { toast.error(err.response ? formatApiError(err.response.data?.detail) : err.message); }
    finally { setGenBusy(false); }
  };

  const saveEdit = async (content) => {
    try {
      const r = await api.put(withOrgId(`/org/campaigns/${selected}/interpretation`, orgId), { content });
      setInterp(r.data.interpretation); toast.success("Edits saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const download = async (kind) => {
    const token = localStorage.getItem("cg_token");
    const path = kind === "pdf" ? `report.pdf` : `export.csv`;
    const r = await fetch(withOrgId(`${API}/org/campaigns/${selected}/${path}`, orgId), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Not enough participants for reporting"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = kind === "pdf" ? `${(overview?.campaigns?.find((x) => x.id === selected)?.name || "campaign").replace(/[^\w-]+/g, "-")}-organizational-authority-culture-portrait.pdf` : "aggregate.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (<div><HeaderNav /><div className="max-w-6xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div></div>);

  const org = overview.organization;
  const campaigns = overview.campaigns;
  const dims = agg?.dimensions || [];
  const aggregate = agg?.aggregate;

  const barData = aggregate ? dims.filter((d) => aggregate[d.key]).map((d) => ({
    key: d.key, name: d.name.split(" ")[0], average: aggregate[d.key].average, variation: aggregate[d.key].variation,
  })) : [];
  const radarData = aggregate ? dims.filter((d) => aggregate[d.key]).map((d) => ({ dim: d.name.split(" ")[0], value: aggregate[d.key].average })) : [];
  const content = interp?.content;

  return (
    <div>
      <HeaderNav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4 cg-fade-up">
          <div>
            <p className="cg-eyebrow mb-2">Organizational Authority Portrait</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6]">{org.organization_name}</h1>
            <p className="mt-2 text-sm text-[#9CA3AF]">Aggregate reporting only · minimum {overview.threshold} responses per subgroup {org.is_demo && "· demo tenant"}</p>
            {saMode && <p className="mt-2 cg-eyebrow text-brass" data-testid="sa-view-badge">Viewing as Super Admin</p>}
          </div>
          {!saMode && (
            <button data-testid="create-campaign-button" onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-5 py-2.5 rounded-full cg-scale-btn hover:bg-amber2">
              <Plus className="w-4 h-4" /> New campaign
            </button>
          )}
        </div>

        {showCreate && (
          <div className="cg-card p-6 mt-6 cg-fade" data-testid="create-campaign-form">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Create campaign</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="campaign-name-input" placeholder="Campaign name (e.g. Leadership Cohort 2027)" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass outline-none" />
              <input data-testid="campaign-cohort-input" placeholder="Cohort name (optional)" value={form.cohort_name}
                onChange={(e) => setForm({ ...form, cohort_name: e.target.value })}
                className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-[#F3F4F6] focus:border-brass outline-none" />
            </div>
            <div className="flex gap-3 mt-4">
              <button data-testid="campaign-save-button" onClick={createCampaign} disabled={!form.name}
                className="bg-brass text-obsidian font-medium px-5 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">Create &amp; get code</button>
              <button onClick={() => setShowCreate(false)} className="text-sm text-[#9CA3AF] px-4">Cancel</button>
            </div>
          </div>
        )}

        {/* campaign selector */}
        {saMode && <CampaignAdmin orgId={orgId} orgName={org.organization_name} onChanged={loadOverview} />}
        <div className="mt-8 flex flex-wrap gap-3" data-testid="campaign-tabs">
          {campaigns.map((c) => (
            <button key={c.id} data-testid={`campaign-tab-${c.id}`} onClick={() => setSelected(c.id)}
              className={`px-4 py-2 rounded-full text-sm cg-scale-btn border ${selected === c.id ? "border-brass bg-[rgba(217,162,95,0.1)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/40"}`}>
              {c.name} <span className="font-mono text-xs text-[#6B7280]">· {c.completed}/{c.invited || c.completed}</span>
              {c.completed >= overview.threshold
                ? <span data-testid={`campaign-elig-${c.id}`} className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[rgba(125,157,134,0.2)] text-[#7D9D86]">eligible</span>
                : <span data-testid={`campaign-elig-${c.id}`} className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#222634] text-[#6B7280]">{c.completed}/{overview.threshold}</span>}
            </button>
          ))}
        </div>

        {selected && (() => {
          const camp = campaigns.find((c) => c.id === selected);
          return (
            <div className="mt-6 space-y-6">
              <div className="cg-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="campaign-code-row">
                <div>
                  <p className="cg-eyebrow mb-1">Invitation code</p>
                  <p className="font-mono text-lg text-brass">{camp?.code}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(camp?.code); toast.success("Code copied"); }}
                  data-testid="copy-code-button" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass cg-scale-btn">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>

              {!saMode && <Invite campaignId={selected} />}

              {!saMode && <WavesPanel campaign={camp} onSaved={loadOverview} />}

              {!saMode && <IntakePanel campaign={camp} />}

              {!saMode && <IntakeAnalytics campaign={camp} />}

              {!saMode && <ReminderControls campaign={camp} onSaved={loadOverview} />}
              {!saMode && <ReminderStats campaign={camp} />}

              {/* metrics */}
              <div className="grid grid-cols-3 gap-4" data-testid="participation-metrics">
                {[["Completed", camp?.completed], ["Invited", camp?.invited || camp?.completed], ["Threshold", overview.threshold]].map(([l, v], i) => (
                  <div key={i} className="cg-card p-5">
                    <p className="cg-eyebrow mb-1">{l}</p>
                    <p className="font-serif text-3xl text-[#F3F4F6]">{v}</p>
                  </div>
                ))}
              </div>

              {agg?.suppressed ? (
                <div className="cg-card p-10 text-center" data-testid="suppressed-notice">
                  <Lock className="w-8 h-8 text-ghostslate mx-auto mb-4" />
                  <p className="font-serif text-2xl text-[#F3F4F6] mb-2">Results suppressed to protect privacy</p>
                  <p className="text-sm text-[#9CA3AF] max-w-md mx-auto">This subgroup has {agg.n} completed assessment{agg.n === 1 ? "" : "s"}, below the minimum of {agg.threshold}. Aggregate results appear once enough participants complete the assessment.</p>
                </div>
              ) : aggregate ? (
                <>
                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="cg-card p-6" data-testid="dimension-distribution-chart">
                      <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Dimension averages</h3>
                      <p className="text-xs text-[#6B7280] mb-4 font-mono">1–5 scale · with within-dimension variation</p>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={barData} margin={{ left: -15 }}>
                          <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={50} />
                          <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
                          <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                          <Bar dataKey="average" radius={[6, 6, 0, 0]}>
                            {barData.map((e) => <Cell key={e.key} fill={DIM_COLOR[e.key]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="cg-card p-6" data-testid="dimension-radar-chart">
                      <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">Seven-dimension profile</h3>
                      <p className="text-xs text-[#6B7280] mb-4 font-mono">Organizational shape</p>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="rgba(255,255,255,0.1)" />
                          <PolarAngleAxis dataKey="dim" tick={{ fill: "#9CA3AF", fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#6B7280", fontSize: 9 }} />
                          <Radar dataKey="value" stroke="#D9A25F" fill="#D9A25F" fillOpacity={0.25} strokeWidth={2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="cg-card p-6" data-testid="distribution-detail">
                    <h3 className="font-serif text-xl text-[#F3F4F6] mb-4">Distributions &amp; variation</h3>
                    <div className="space-y-3">
                      {dims.filter((d) => aggregate[d.key]).map((d) => {
                        const a = aggregate[d.key]; const totalN = a.n;
                        return (
                          <div key={d.key} className="flex items-center gap-4">
                            <span className="text-sm text-[#9CA3AF] w-44 shrink-0">{d.name}</span>
                            <div className="flex-1 flex h-4 rounded-full overflow-hidden bg-[#0D0E12]">
                              {Object.entries(a.distribution).map(([bucket, cnt], bi) => (
                                <div key={bucket} title={`${bucket}: ${cnt}`} style={{ width: `${(cnt / totalN) * 100}%`, backgroundColor: DIM_COLOR[d.key], opacity: 0.35 + bi * 0.2 }} />
                              ))}
                            </div>
                            <span className="font-mono text-xs text-[#6B7280] w-24 text-right">σ {a.variation}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {saMode ? (
                    <div className="cg-card p-7" data-testid="org-narrative">
                      <ReportEditor interp={interp} onGenerate={generate} genBusy={genBusy} onSave={saveEdit} onPdf={() => download("pdf")} n={agg.n} prefix="campaign" />
                      <button onClick={() => download("csv")} data-testid="org-csv-button" className="mt-5 inline-flex items-center gap-2 border border-[rgba(255,255,255,0.15)] text-sm px-4 py-2 rounded-full hover:border-brass cg-scale-btn"><Download className="w-4 h-4" /> Aggregate CSV</button>
                    </div>
                  ) : (
                    <>
                      <ReportNotice />
                      <button onClick={() => download("csv")} data-testid="org-csv-button" className="inline-flex items-center gap-2 border border-[rgba(255,255,255,0.15)] text-sm px-4 py-2 rounded-full hover:border-brass cg-scale-btn"><Download className="w-4 h-4" /> Aggregate CSV</button>
                    </>
                  )}
                </>
              ) : (
                <p className="cg-eyebrow animate-pulse">Loading aggregate…</p>
              )}
            </div>
          );
        })()}

        <OrgWideProfile orgId={orgId} orgName={org.organization_name} threshold={overview.threshold} campaigns={campaigns} />
        <CohortCompare campaigns={campaigns} threshold={overview.threshold} dims={dims.length ? dims : null} orgId={orgId} />
        <LongitudinalCompare campaigns={campaigns} threshold={overview.threshold} orgId={orgId} />
      </div>
    </div>
  );
}

function Invite({ campaignId }) {
  const [emails, setEmails] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => { if (campaignId) api.get(`/org/campaigns/${campaignId}/invitations`).then((r) => setList(r.data.invitations)); }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const parseEmails = (text) => {
    const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    return Array.from(new Set((text.match(re) || []).map((e) => e.toLowerCase())));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const found = parseEmails(text);
    if (!found.length) { toast.error("No email addresses found in file"); return; }
    setEmails((prev) => parseEmails(prev + "\n" + found.join("\n")).join("\n"));
    toast.success(`${found.length} email${found.length === 1 ? "" : "s"} loaded from ${file.name}`);
    e.target.value = "";
  };

  const send = async () => {
    const parsed = parseEmails(emails);
    if (!parsed.length) { toast.error("Enter or upload at least one email"); return; }
    setBusy(true);
    try {
      let sent = 0, failed = 0;
      for (let i = 0; i < parsed.length; i += 100) {
        const batch = parsed.slice(i, i + 100);
        const r = await api.post(`/org/campaigns/${campaignId}/invitations`, { emails: batch });
        sent += r.data.results.filter((x) => x.status === "sent").length;
        failed += r.data.results.filter((x) => x.status !== "sent").length;
      }
      toast.success(`${sent} invitation${sent === 1 ? "" : "s"} sent${failed ? `, ${failed} skipped` : ""}`);
      setEmails(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const statusColor = { accepted: "text-[#7D9D86]", sent: "text-brass", reminded: "text-amber2", failed: "text-terracotta", invalid: "text-terracotta" };
  const exportNotStarted = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(`${API}/org/campaigns/${campaignId}/not-started-export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not export list"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "not-started-invitees.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Not-started list exported");
  };
  const FILTERS = [["all", "All"], ["not_started", "Not started"], ["reminded", "Reminded"], ["accepted", "Completed join"], ["failed", "Failed"]];
  const matches = (iv) => filter === "all" ? true
    : filter === "not_started" ? (iv.status === "sent")
    : iv.status === filter;
  const shown = list.filter(matches);
  return (
    <div className="cg-card p-6" data-testid="invite-panel">
      <div className="flex items-center gap-2 mb-3"><Mail className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Invite participants</h3></div>
      <p className="text-xs text-[#6B7280] mb-3 font-mono">Each participant receives a secure single-use magic-link by email. Their portrait stays private; you see aggregate only.</p>
      <textarea data-testid="invite-emails-input" className="w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-sm text-[#F3F4F6] focus:border-brass outline-none h-20"
        placeholder="Enter emails separated by commas, spaces or new lines — or upload a CSV" value={emails} onChange={(e) => setEmails(e.target.value)} />
      <div className="mt-3 flex flex-wrap gap-3 items-center">
        <button data-testid="invite-send-button" onClick={send} disabled={busy || !emails.trim()}
          className="inline-flex items-center gap-2 bg-brass text-obsidian text-sm font-medium px-5 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">
          <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send invitations"}
        </button>
        <label data-testid="invite-csv-label" className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass cursor-pointer">
          <Upload className="w-4 h-4" /> Upload CSV
          <input data-testid="invite-csv-input" type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={onFile} />
        </label>
        <button data-testid="invite-export-button" type="button" onClick={exportNotStarted}
          className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass">
          <Download className="w-4 h-4" /> Export not started
        </button>
        <span className="text-xs text-[#6B7280]">CSV with an email column, or any list — we extract every address.</span>
      </div>
      {list.length > 0 && (
        <div className="mt-5" data-testid="invite-list">
          <div className="flex flex-wrap gap-2 mb-3" data-testid="invite-filters">
            {FILTERS.map(([k, lbl]) => (
              <button key={k} data-testid={`invite-filter-${k}`} onClick={() => setFilter(k)}
                className={`text-xs px-3 py-1 rounded-full cg-scale-btn border ${filter === k ? "border-brass bg-[rgba(217,162,95,0.1)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/40"}`}>
                {lbl} <span className="text-[#6B7280]">{list.filter((iv) => (k === "all" ? true : k === "not_started" ? iv.status === "sent" : iv.status === k)).length}</span>
              </button>
            ))}
          </div>
          <p className="cg-eyebrow mb-2">{shown.length} shown</p>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {shown.map((iv) => (
              <div key={iv.id} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-1.5">
                <span className="text-[#9CA3AF]">{iv.email}</span>
                <span className={statusColor[iv.status] || "text-[#6B7280]"}>{iv.status}{iv.reminder_count ? ` ·${iv.reminder_count}` : ""}</span>
              </div>
            ))}
            {shown.length === 0 && <p className="text-xs text-[#6B7280] py-2">No invitations match this filter.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function ReminderStats({ campaign }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/org/campaigns/${campaign.id}/reminder-stats`).then((r) => setS(r.data)).catch(() => {}); }, [campaign.id]);
  if (!s) return null;
  return <p className="text-xs text-[#6B7280] font-mono mt-2 ml-1" data-testid="reminder-stats">Reminders sent — resume: {s.resume_reminders_sent} · invites: {s.invite_reminders_sent}</p>;
}

function IntakeAnalytics({ campaign }) {
  const [data, setData] = useState(null);
  const [fk, setFk] = useState("");
  useEffect(() => {
    api.get(`/org/intake-breakdown?campaign_ids=${campaign.id}`).then((r) => { setData(r.data); if (r.data.select_fields[0]) setFk(r.data.select_fields[0].key); }).catch(() => {});
  }, [campaign.id]);
  useEffect(() => { if (fk) api.get(`/org/intake-breakdown?campaign_ids=${campaign.id}&field_key=${fk}`).then((r) => setData(r.data)).catch(() => {}); }, [fk, campaign.id]);
  if (!data || !data.select_fields.length) return null;
  const dims = data.dimensions || [];
  const shownGroups = data.groups.filter((g) => !g.suppressed && g.aggregate);
  const CHART_COLORS = ["#D9A25F", "#6B8CBE", "#7D9D86", "#C08497", "#B0A160", "#8E7CC3", "#5FA8A0"];
  const chartData = dims.map((d) => {
    const row = { name: d.name.split(" ")[0] };
    shownGroups.forEach((g) => { row[g.value] = g.aggregate?.[d.key]?.average ?? null; });
    return row;
  });
  return (
    <div className="cg-card p-6 mt-6" data-testid="intake-analytics">
      <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Intake breakdown</h3></div>
      <p className="text-sm text-[#6B7280] mb-4">Aggregate averages grouped by an intake answer. Groups below the privacy threshold ({data.threshold}) are suppressed; individual answers are never shown.</p>
      <select className={`${inp} max-w-[260px] mb-4`} data-testid="intake-analytics-field" value={fk} onChange={(e) => setFk(e.target.value)}>
        {data.select_fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      {data.groups.length === 0 && <p className="text-sm text-[#9CA3AF]">No responses yet for this question.</p>}
      {shownGroups.length >= 1 && (
        <div className="mb-5" data-testid="intake-analytics-chart">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ left: -15 }} barGap={2}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {shownGroups.map((g, i) => (
                <Bar key={g.value} dataKey={g.value} name={`${g.value} (n=${g.n})`} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="space-y-3">
        {data.groups.map((g) => (
          <div key={g.value} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3" data-testid={`intake-group-${g.value}`}>
            <p className="text-sm text-[#E8EAEE] mb-2">{g.value} <span className="text-[#6B7280] font-mono">· n={g.n}</span> {g.suppressed && <span className="text-amber2 text-xs">· suppressed (below threshold)</span>}</p>
            {!g.suppressed && (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {dims.map((d) => (
                  <div key={d.key} className="flex justify-between text-sm">
                    <span className="text-[#9CA3AF]">{d.name}</span>
                    <span className="font-mono text-[#B9BDC7]">{g.aggregate?.[d.key]?.average ?? "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function IntakePanel({ campaign }) {
  const [fields, setFields] = useState(campaign.intake_fields || []);
  const [saving, setSaving] = useState(false);
  const setF = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const add = () => setFields([...fields, { key: "", label: "", type: "text", options: [], required: false }]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.patch(`/org/campaigns/${campaign.id}/intake-fields`, { intake_fields: fields }); setFields(r.data.intake_fields); toast.success("Intake questions saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="cg-card p-6 mt-6" data-testid="intake-panel">
      <div className="flex items-center gap-2 mb-1"><ListChecks className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Campaign intake questions</h3></div>
      <p className="text-sm text-[#6B7280] mb-5">Optional questions asked once, right after a participant joins. Individual answers are never shown to you; subgroup reporting still respects the minimum privacy threshold.</p>
      <div className="space-y-3">
        {fields.map((f, i) => (
          <div key={i} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3 space-y-2" data-testid={`intake-field-${i}`}>
            <div className="flex flex-wrap gap-2 items-center">
              <input className={`${inp} max-w-[240px]`} data-testid={`intake-label-${i}`} placeholder="Question label" value={f.label} onChange={(e) => setF(i, { label: e.target.value })} />
              <select className={`${inp} max-w-[150px]`} value={f.type} onChange={(e) => setF(i, { type: e.target.value })}>
                <option value="text">Short text</option><option value="select">Dropdown</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-[#9CA3AF]"><input type="checkbox" checked={!!f.required} onChange={(e) => setF(i, { required: e.target.checked })} className="w-4 h-4 accent-[#D9A25F]" /> required</label>
              <button className="text-xs text-[#9CA3AF] hover:text-terracotta ml-auto" onClick={() => setFields(fields.filter((_, j) => j !== i))}>Remove</button>
            </div>
            {f.type === "select" && (
              <input className={inp} data-testid={`intake-options-${i}`} placeholder="Options, comma-separated" value={(f.options || []).join(", ")} onChange={(e) => setF(i, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button className="border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full hover:border-brass cg-scale-btn" data-testid="intake-add" onClick={add}>+ Add question</button>
        <button className="bg-brass text-obsidian font-medium px-5 py-2 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-50 text-sm" data-testid="intake-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save intake questions"}</button>
      </div>
    </div>
  );
}

function WavesPanel({ campaign, onSaved }) {
  const [waves, setWaves] = useState([]);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ label: "", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [sel, setSel] = useState(new Set());
  const [remId, setRemId] = useState(null);
  const [remForm, setRemForm] = useState({ custom: false, reminders_enabled: true, reminder_after_days: 3, reminder_spacing_days: 3, max_reminders: 2 });
  const load = useCallback(() => {
    api.get(`/org/campaigns/${campaign.id}/waves`).then((r) => { setWaves(r.data.waves || []); setCurrent(r.data.current_wave_label); setSel(new Set()); });
  }, [campaign.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/org/campaigns/${campaign.id}/waves`, { label: form.label.trim(), start_date: form.start_date || null, end_date: form.end_date || null });
      toast.success(`Assessment period "${form.label.trim()}" opened`);
      setForm({ label: "", start_date: "", end_date: "" }); setOpen(false); load(); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const patchWave = async (w, payload, msg) => {
    try {
      await api.patch(`/org/campaigns/${campaign.id}/waves/${w.id}`, payload);
      if (msg) toast.success(msg);
      setEditId(null); load(); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (w) => {
    if (!window.confirm(`Delete assessment period "${w.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/org/campaigns/${campaign.id}/waves/${w.id}`);
      toast.success(`Assessment period "${w.label}" deleted`); load(); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulk = async (action) => {
    try {
      const r = await api.post(`/org/campaigns/${campaign.id}/waves/bulk`, { action, wave_ids: Array.from(sel) });
      toast.success(`${r.data.changed} assessment period${r.data.changed === 1 ? "" : "s"} ${action === "close" ? "closed" : "reopened"}`);
      load(); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const openReminders = (w) => {
    setRemId(remId === w.id ? null : w.id);
    setRemForm({
      custom: w.reminders_enabled != null,
      reminders_enabled: w.reminders_enabled ?? true,
      reminder_after_days: w.reminder_after_days ?? 3,
      reminder_spacing_days: w.reminder_spacing_days ?? 3,
      max_reminders: w.max_reminders ?? 2,
    });
  };
  const submitReminders = async (w) => {
    const payload = remForm.custom
      ? { reminders_enabled: remForm.reminders_enabled, reminder_after_days: Number(remForm.reminder_after_days), reminder_spacing_days: Number(remForm.reminder_spacing_days), max_reminders: Number(remForm.max_reminders) }
      : { inherit: true };
    try {
      await api.patch(`/org/campaigns/${campaign.id}/waves/${w.id}/reminder-settings`, payload);
      toast.success(remForm.custom ? "Assessment period reminder schedule saved" : "Assessment period now inherits campaign reminders");
      setRemId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const inp = "bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] focus:border-brass outline-none";
  const numCls = "w-20 bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#F3F4F6] focus:border-brass outline-none";
  return (
    <div className="cg-card p-6" data-testid="waves-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Assessment periods</h3></div>
        <button data-testid="wave-open-button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full cg-scale-btn hover:border-brass"><Plus className="w-4 h-4" /> New assessment period</button>
      </div>
      <p className="text-xs text-[#6B7280] mb-4 font-mono">Open a new dated assessment period to reassess this cohort over time (e.g. Initial Assessment, then a Reassessment). New completions are tagged to the current period and appear in "Compare over time". Rename, set current, close, delete, give a period its own reminder schedule, or select several to close in bulk.</p>
      {open && (
        <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4 mb-4" data-testid="wave-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="wave-label-input" className={inp} placeholder="Period name (e.g. Initial Assessment — Jan 2027)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <DateField testId="wave-start-input" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} placeholder="Start date" />
            <DateField testId="wave-end-input" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} placeholder="End date" />
          </div>
          <button data-testid="wave-create-button" onClick={create} disabled={busy || !form.label.trim()} className="mt-3 bg-brass text-obsidian text-sm font-medium px-5 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">{busy ? "Opening…" : "Open period"}</button>
        </div>
      )}
      {sel.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-[rgba(217,162,95,0.08)] border border-[var(--cg-border)] rounded-xl" data-testid="wave-bulk-bar">
          <span className="text-sm text-[#E8EAEE]">{sel.size} selected</span>
          <button data-testid="wave-bulk-close" onClick={() => bulk("close")} className="text-xs text-[#9CA3AF] hover:text-brass border border-[var(--cg-border)] rounded-full px-3 py-1.5 cg-scale-btn">Close selected</button>
          <button data-testid="wave-bulk-reopen" onClick={() => bulk("reopen")} className="text-xs text-[#9CA3AF] hover:text-brass border border-[var(--cg-border)] rounded-full px-3 py-1.5 cg-scale-btn">Reopen selected</button>
          <button onClick={() => setSel(new Set())} className="text-xs text-[#6B7280] hover:text-brass ml-auto">Clear</button>
        </div>
      )}
      <div className="space-y-1" data-testid="waves-list">
        {waves.length === 0 && <p className="text-sm text-[#9CA3AF]">No assessment periods yet. Completions so far are grouped as a baseline; open a period to start tracking change over time.</p>}
        {waves.map((w) => {
          const isCurrent = w.label === current;
          const active = w.active !== false;
          const hasCustomReminders = w.reminders_enabled != null;
          return (
            <div key={w.id} data-testid={`wave-row-${w.id}`} className="border-b border-[var(--cg-border)] py-2.5 last:border-0">
              <div className="flex flex-wrap justify-between items-center gap-2">
                {editId === w.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input data-testid="wave-rename-input" className={`${inp} flex-1`} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
                    <button data-testid="wave-rename-save" onClick={() => patchWave(w, { label: editLabel.trim() }, "Assessment period renamed")} disabled={!editLabel.trim()} className="text-brass hover:text-amber2 disabled:opacity-40"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="text-[#9CA3AF] hover:text-brass"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-3 min-w-0">
                      <input type="checkbox" data-testid={`wave-select-${w.id}`} checked={sel.has(w.id)} onChange={() => toggleSel(w.id)} className="mt-1 w-4 h-4 accent-[#D9A25F]" />
                      <div className="min-w-0">
                        <span className="text-[#E8EAEE]">{w.label}</span>
                        {isCurrent && <span className="cg-eyebrow ml-2 text-brass">current</span>}
                        {!active && <span className="cg-eyebrow ml-2 text-[#6B7280]">closed</span>}
                        {hasCustomReminders && <span className="cg-eyebrow ml-2 text-amber2">own schedule</span>}
                        <span className="block text-xs text-[#6B7280] font-mono mt-0.5">{w.start_date || "—"}{w.end_date ? ` → ${w.end_date}` : ""} · {w.completed ?? 0} completed</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {active && !isCurrent && (
                        <button data-testid={`wave-setcurrent-${w.id}`} onClick={() => patchWave(w, { set_current: true }, `"${w.label}" is now the current period`)} title="Set as current" className="text-xs text-[#9CA3AF] hover:text-brass border border-[var(--cg-border)] rounded-full px-2.5 py-1 cg-scale-btn">Set current</button>
                      )}
                      <button data-testid={`wave-reminders-${w.id}`} onClick={() => openReminders(w)} title="Reminder schedule" className={`p-1 ${hasCustomReminders ? "text-amber2" : "text-[#9CA3AF]"} hover:text-brass`}><BellRing className="w-3.5 h-3.5" /></button>
                      <button data-testid={`wave-edit-${w.id}`} onClick={() => { setEditId(w.id); setEditLabel(w.label); }} title="Rename" className="text-[#9CA3AF] hover:text-brass p-1"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`wave-close-${w.id}`} onClick={() => patchWave(w, { active: !active }, active ? "Assessment period closed" : "Assessment period reopened")} title={active ? "Close period" : "Reopen period"} className="text-[#9CA3AF] hover:text-brass p-1">{active ? <XCircle className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}</button>
                      <button data-testid={`wave-delete-${w.id}`} onClick={() => del(w)} title="Delete period" className="text-[#9CA3AF] hover:text-terracotta p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </>
                )}
              </div>
              {remId === w.id && (
                <div className="mt-3 bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid={`wave-reminder-editor-${w.id}`}>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input type="checkbox" data-testid={`wave-reminder-custom-${w.id}`} checked={remForm.custom} onChange={(e) => setRemForm({ ...remForm, custom: e.target.checked })} className="w-4 h-4 accent-[#D9A25F]" />
                    <span className="text-sm text-[#9CA3AF]">Use a custom reminder schedule for this assessment period (anchored to its start date)</span>
                  </label>
                  {remForm.custom && (
                    <div className="grid sm:grid-cols-4 gap-3 items-end">
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-[#9CA3AF]">
                        <input type="checkbox" data-testid={`wave-reminder-enabled-${w.id}`} checked={remForm.reminders_enabled} onChange={(e) => setRemForm({ ...remForm, reminders_enabled: e.target.checked })} className="w-4 h-4 accent-[#D9A25F]" /> On
                      </label>
                      <label className="text-xs text-[#9CA3AF]">First after (days)<br /><input data-testid={`wave-reminder-after-${w.id}`} type="number" min="1" max="30" className={`${numCls} mt-1`} value={remForm.reminder_after_days} onChange={(e) => setRemForm({ ...remForm, reminder_after_days: e.target.value })} /></label>
                      <label className="text-xs text-[#9CA3AF]">Repeat every (days)<br /><input data-testid={`wave-reminder-spacing-${w.id}`} type="number" min="1" max="30" className={`${numCls} mt-1`} value={remForm.reminder_spacing_days} onChange={(e) => setRemForm({ ...remForm, reminder_spacing_days: e.target.value })} /></label>
                      <label className="text-xs text-[#9CA3AF]">Max reminders<br /><input data-testid={`wave-reminder-max-${w.id}`} type="number" min="1" max="5" className={`${numCls} mt-1`} value={remForm.max_reminders} onChange={(e) => setRemForm({ ...remForm, max_reminders: e.target.value })} /></label>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`wave-reminder-save-${w.id}`} onClick={() => submitReminders(w)} className="bg-brass text-obsidian text-sm font-medium px-4 py-2 rounded-full cg-scale-btn hover:bg-amber2">Save schedule</button>
                    <button onClick={() => setRemId(null)} className="text-sm text-[#9CA3AF] px-3">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReminderControls({ campaign, onSaved }) {
  const [s, setS] = useState({
    reminders_enabled: campaign.reminders_enabled ?? true,
    reminder_after_days: campaign.reminder_after_days ?? 3,
    reminder_spacing_days: campaign.reminder_spacing_days ?? 3,
    max_reminders: campaign.max_reminders ?? 2,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setS({
      reminders_enabled: campaign.reminders_enabled ?? true,
      reminder_after_days: campaign.reminder_after_days ?? 3,
      reminder_spacing_days: campaign.reminder_spacing_days ?? 3,
      max_reminders: campaign.max_reminders ?? 2,
    });
  }, [campaign.id]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/org/campaigns/${campaign.id}/reminder-settings`, {
        reminders_enabled: s.reminders_enabled,
        reminder_after_days: Number(s.reminder_after_days),
        reminder_spacing_days: Number(s.reminder_spacing_days),
        max_reminders: Number(s.max_reminders),
      });
      setS({
        reminders_enabled: r.data.reminders_enabled,
        reminder_after_days: r.data.reminder_after_days,
        reminder_spacing_days: r.data.reminder_spacing_days,
        max_reminders: r.data.max_reminders,
      });
      toast.success("Reminder settings saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState("");
  const [testBusy, setTestBusy] = useState("");
  const showPreview = async (kind) => {
    setPreviewBusy(kind);
    try { const r = await api.get(`/org/campaigns/${campaign.id}/reminder-preview?kind=${kind}`); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreviewBusy(""); }
  };
  const sendTest = async (kind) => {
    setTestBusy(kind);
    try { const r = await api.post(`/org/campaigns/${campaign.id}/reminder-test?kind=${kind}`); toast.success(`Test ${kind === "resume" ? "resume-reminder" : "reminder"} sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTestBusy(""); }
  };
  const numCls = "w-20 bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#F3F4F6] focus:border-brass outline-none";
  return (
    <div className="cg-card p-6" data-testid="reminder-controls">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2"><BellRing className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Reminder settings</h3></div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="reminder-toggle-label">
          <input type="checkbox" data-testid="reminder-enabled-toggle" checked={s.reminders_enabled} onChange={(e) => setS({ ...s, reminders_enabled: e.target.checked })} className="w-5 h-5 accent-[#D9A25F]" />
          <span className="text-sm text-[#9CA3AF]">{s.reminders_enabled ? "Reminders on" : "Reminders off"}</span>
        </label>
      </div>
      <p className="text-xs text-[#6B7280] mb-4 font-mono">Automatic gentle nudges to invitees who haven't completed. A daily job applies these rules.</p>
      <div className={`grid sm:grid-cols-3 gap-4 ${s.reminders_enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <label className="text-sm text-[#9CA3AF]">First after (days)<br /><input data-testid="reminder-after-input" type="number" min="1" max="30" className={`${numCls} mt-1.5`} value={s.reminder_after_days} onChange={(e) => setS({ ...s, reminder_after_days: e.target.value })} /></label>
        <label className="text-sm text-[#9CA3AF]">Repeat every (days)<br /><input data-testid="reminder-spacing-input" type="number" min="1" max="30" className={`${numCls} mt-1.5`} value={s.reminder_spacing_days} onChange={(e) => setS({ ...s, reminder_spacing_days: e.target.value })} /></label>
        <label className="text-sm text-[#9CA3AF]">Max reminders<br /><input data-testid="reminder-max-input" type="number" min="1" max="5" className={`${numCls} mt-1.5`} value={s.max_reminders} onChange={(e) => setS({ ...s, max_reminders: e.target.value })} /></label>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button data-testid="reminder-save-button" onClick={save} disabled={busy}
          className="bg-brass text-obsidian text-sm font-medium px-5 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">
          {busy ? "Saving…" : "Save settings"}
        </button>
        <button data-testid="reminder-preview-button" onClick={() => showPreview("invite")} disabled={!!previewBusy}
          className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40">
          <Eye className="w-4 h-4" /> {previewBusy === "invite" ? "Loading…" : "Preview invite reminder"}
        </button>
        <button data-testid="reminder-test-button" onClick={() => sendTest("invite")} disabled={!!testBusy}
          className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40">
          <Send className="w-4 h-4" /> {testBusy === "invite" ? "Sending…" : "Send invite test to me"}
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--cg-border)]">
        <p className="text-xs text-[#6B7280] mb-3 font-mono">Resume reminder — the nudge participants get when they've started but not finished. See exactly what they receive.</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="resume-reminder-preview-button" onClick={() => showPreview("resume")} disabled={!!previewBusy}
            className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40">
            <Eye className="w-4 h-4" /> {previewBusy === "resume" ? "Loading…" : "Preview resume reminder"}
          </button>
          <button data-testid="resume-reminder-test-button" onClick={() => sendTest("resume")} disabled={!!testBusy}
            className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40">
            <Send className="w-4 h-4" /> {testBusy === "resume" ? "Sending…" : "Send resume test to me"}
          </button>
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="reminder-preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-[#14161F] border border-[var(--cg-border)] rounded-2xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--cg-border)]">
              <div><p className="cg-eyebrow">{preview.kind === "resume" ? "Resume reminder preview" : "Invite reminder preview"}</p><p className="text-sm text-[#F3F4F6] mt-1">{preview.subject}</p></div>
              <button data-testid="reminder-preview-close" onClick={() => setPreview(null)} className="text-[#9CA3AF] hover:text-brass"><X className="w-5 h-5" /></button>
            </div>
            <iframe title="reminder-preview" srcDoc={preview.html} className="w-full flex-1 min-h-[440px] bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function CohortCompare({ campaigns, threshold, dims, orgId }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const fetchAgg = async (cid, setter) => {
    if (!cid) { setter(null); return; }
    try { const r = await api.get(withOrgId(`/org/campaigns/${cid}/aggregate`, orgId)); setter(r.data); } catch { setter(null); }
  };
  useEffect(() => { fetchAgg(a, setDataA); }, [a]); // eslint-disable-line
  useEffect(() => { fetchAgg(b, setDataB); }, [b]); // eslint-disable-line

  const usableDims = dims || dataA?.dimensions || dataB?.dimensions || [];
  const nameA = campaigns.find((c) => c.id === a)?.name || "Cohort A";
  const nameB = campaigns.find((c) => c.id === b)?.name || "Cohort B";
  const bothReady = dataA && dataB && !dataA.suppressed && !dataB.suppressed;
  const chartData = bothReady ? usableDims.filter((d) => dataA.aggregate[d.key] && dataB.aggregate[d.key]).map((d) => ({
    name: d.name.split(" ")[0], A: dataA.aggregate[d.key].average, B: dataB.aggregate[d.key].average,
  })) : [];

  return (
    <div className="cg-card p-7 mt-8" data-testid="cohort-compare">
      <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-brass" /><h3 className="font-serif text-2xl text-[#F3F4F6]">Compare cohorts</h3></div>
      <p className="text-sm text-[#6B7280] mb-5">See where authority patterns diverge across two eligible cohorts. Cohorts below the minimum of {threshold} are suppressed.</p>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <select data-testid="compare-select-a" className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-sm text-[#F3F4F6] focus:border-brass outline-none" value={a} onChange={(e) => setA(e.target.value)}>
          <option value="">Select cohort A…</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="compare-select-b" className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-sm text-[#F3F4F6] focus:border-brass outline-none" value={b} onChange={(e) => setB(e.target.value)}>
          <option value="">Select cohort B…</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {a && dataA?.suppressed && <p className="text-sm text-terracotta mb-2" data-testid="compare-suppressed-a">{nameA}: suppressed ({dataA.n}/{threshold}).</p>}
      {b && dataB?.suppressed && <p className="text-sm text-terracotta mb-2" data-testid="compare-suppressed-b">{nameB}: suppressed ({dataB.n}/{threshold}).</p>}
      {bothReady ? (
        <div data-testid="compare-chart">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ left: -15 }}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="A" name={nameA} fill="#D9A25F" radius={[5, 5, 0, 0]} />
              <Bar dataKey="B" name={nameB} fill="#8A99AD" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-6 justify-center mt-2 text-xs font-mono">
            <span className="text-brass">■ {nameA} (n={dataA.n})</span>
            <span className="text-ghostslate">■ {nameB} (n={dataB.n})</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#9CA3AF]" data-testid="compare-hint">Select two eligible cohorts to see dimension averages side by side.</p>
      )}
    </div>
  );
}


const WAVE_COLORS = ["#D9A25F", "#8A99AD", "#7D9D86", "#C46243"];

function LongitudinalCompare({ campaigns, threshold, orgId }) {
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cid) { setData(null); return; }
    api.get(withOrgId(`/org/campaigns/${cid}/longitudinal`, orgId)).then((r) => setData(r.data)).catch(() => setData(null));
  }, [cid, orgId]);

  const waves = (data?.waves || []).filter((w) => !w.suppressed);
  const dims = data?.dimensions || [];
  const chartData = waves.length >= 2 ? dims.filter((d) => waves.every((w) => w.aggregate[d.key])).map((d) => {
    const row = { name: d.name.split(" ")[0] };
    waves.forEach((w, i) => { row[`w${i}`] = w.aggregate[d.key].average; });
    return row;
  }) : [];

  return (
    <div className="cg-card p-7 mt-8" data-testid="longitudinal-compare">
      <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-brass" /><h3 className="font-serif text-2xl text-[#F3F4F6]">Compare over time</h3></div>
      <p className="text-sm text-[#6B7280] mb-5">See how a cohort's authority patterns shift across assessment periods. Each period is suppressed unless it meets the minimum of {threshold}.</p>
      <select data-testid="longitudinal-select" className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-sm text-[#F3F4F6] focus:border-brass outline-none w-full sm:max-w-md mb-5" value={cid} onChange={(e) => setCid(e.target.value)}>
        <option value="">Select a cohort with repeat assessments…</option>
        {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {data && (data.waves || []).some((w) => w.suppressed) && (
        <p className="text-sm text-terracotta mb-3" data-testid="longitudinal-suppressed">Some assessment periods are below the minimum of {threshold} and are suppressed.</p>
      )}
      {chartData.length ? (
        <div data-testid="longitudinal-chart">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ left: -15 }}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              {waves.map((w, i) => <Bar key={i} dataKey={`w${i}`} name={w.label} fill={WAVE_COLORS[i % WAVE_COLORS.length]} radius={[5, 5, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-5 justify-center mt-2 text-xs font-mono">
            {waves.map((w, i) => <span key={i} style={{ color: WAVE_COLORS[i % WAVE_COLORS.length] }}>■ {w.label} (n={w.n})</span>)}
          </div>
        </div>
      ) : data ? (
        <p className="text-sm text-[#9CA3AF]" data-testid="longitudinal-hint">This cohort needs at least two eligible assessment periods to compare over time.</p>
      ) : null}
    </div>
  );
}


function OrgWideProfile({ orgId, orgName, threshold, campaigns }) {
  const [candidates, setCandidates] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [selected, setSelected] = useState(null);
  const [version, setVersion] = useState(null);
  const [data, setData] = useState(null);
  const [interp, setInterp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cohort, setCohort] = useState("");
  const completedById = {};
  (campaigns || []).forEach((c) => { completedById[c.id] = c.completed || 0; });

  const init = useCallback(async () => {
    try {
      const r = await api.get(withOrgId("/org/aggregate", orgId));
      setCandidates(r.data.included_campaigns || []);
      setExcluded(r.data.excluded_campaigns || []);
      setVersion(r.data.version);
      setSelected(new Set((r.data.included_campaigns || []).map((c) => c.id)));
      setData(r.data);
      if (orgId) {
        const ir = await api.get(withOrgId("/org/interpretation", orgId));
        setInterp(ir.data.interpretation);
        if (ir.data.job?.status === "running") {
          setBusy(true);
          waitForDraft(() => api.get(withOrgId("/org/interpretation", orgId)))
            .then(setInterp).catch((e) => toast.error(e.message)).finally(() => setBusy(false));
        }
      }
    } catch { /* ignore */ }
  }, [orgId]);
  useEffect(() => { init(); }, [init]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selIds = selected ? Array.from(selected) : [];
  const buildQ = () => {
    const p = new URLSearchParams();
    p.set("campaign_ids", selIds.length ? selIds.join(",") : "__none__");
    if (cohort) p.set("cohort", cohort);
    return `?${p.toString()}`;
  };
  useEffect(() => {
    if (selected === null) return;
    api.get(withOrgId(`/org/aggregate${buildQ()}`, orgId)).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, cohort, orgId]);

  const qParam = buildQ();
  const generate = async () => {
    setBusy(true);
    try {
      await api.post(withOrgId(`/org/interpretation${qParam}`, orgId));
      toast.message("Drafting the report… this can take a minute or two");
      setInterp(await waitForDraft(() => api.get(withOrgId("/org/interpretation", orgId))));
      toast.success("Organization-wide report draft generated");
    }
    catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  const saveEdit = async (content) => {
    try { const r = await api.put(withOrgId("/org/interpretation", orgId), { content }); setInterp(r.data.interpretation); toast.success("Edits saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadPdf = async () => {
    const token = localStorage.getItem("cg_token");
    const r = await fetch(withOrgId(`${API}/org/report.pdf${qParam}`, orgId), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Not enough participants for reporting"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "organization-wide-portrait.pdf"; a.click(); URL.revokeObjectURL(url);
  };

  if (!candidates) return null;
  const saMode = !!orgId;
  const dims = data?.dimensions || [];
  const agg = data?.aggregate;
  const barData = agg ? dims.filter((d) => agg[d.key]).map((d) => ({ key: d.key, name: d.name.split(" ")[0], average: agg[d.key].average })) : [];
  return (
    <div className="cg-card p-7 mt-8" data-testid="org-wide-profile">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-brass" /><h3 className="font-serif text-2xl text-[#F3F4F6]">Organization-wide profile</h3></div>
      </div>
      <p className="text-sm text-[#6B7280] mb-4">Choose which campaigns feed this portrait. Only campaigns on the same assessment version (<span className="font-mono">{version}</span>) can be combined.</p>

      <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4 mb-5" data-testid="orgwide-campaign-picker">
        <p className="cg-eyebrow mb-3">Campaigns in this profile · {selIds.length}/{candidates.length} selected</p>
        <div className="space-y-2">
          {candidates.map((c) => {
            const n = completedById[c.id] ?? 0;
            const elig = n >= threshold;
            return (
              <label key={c.id} data-testid={`orgwide-pick-${c.id}`} className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="flex items-center gap-2 text-sm text-[#E8EAEE]">
                  <input type="checkbox" checked={selected?.has(c.id) || false} onChange={() => toggle(c.id)} className="w-4 h-4 accent-[#D9A25F]" />
                  {c.name}
                </span>
                {elig
                  ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(125,157,134,0.2)] text-[#7D9D86]">eligible · {n}</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#222634] text-[#6B7280]">{n}/{threshold}</span>}
              </label>
            );
          })}
          {candidates.length === 0 && <p className="text-sm text-[#9CA3AF]">No campaigns on the active assessment version yet.</p>}
        </div>
        {excluded.length > 0 && (
          <p className="text-xs text-[#6B7280] mt-3 font-mono" data-testid="orgwide-excluded">{excluded.length} campaign(s) excluded — different assessment version: {excluded.map((e) => `${e.name} (${e.assessment_version})`).join(", ")}</p>
        )}
        {(data?.cohorts || []).length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="cg-eyebrow">Team / business unit</label>
            <select data-testid="orgwide-cohort-select" value={cohort} onChange={(e) => setCohort(e.target.value)} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#F3F4F6]">
              <option value="">All (pooled)</option>
              {data.cohorts.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
            </select>
          </div>
        )}
      </div>

      {data?.suppressed ? (
        <div className="text-center py-8" data-testid="orgwide-suppressed"><Lock className="w-7 h-7 text-ghostslate mx-auto mb-3" /><p className="text-sm text-[#9CA3AF]">Suppressed — {data.n}/{threshold} completed across the selected campaigns. Select more campaigns or wait for more completions.</p></div>
      ) : agg ? (
        <>
          <p className="cg-eyebrow mb-3">{data.n} completed assessments pooled</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData} margin={{ left: -15 }}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="average" radius={[6, 6, 0, 0]}>{barData.map((e) => <Cell key={e.key} fill={DIM_COLOR[e.key]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-5 space-y-3" data-testid="orgwide-distributions">
            {dims.filter((d) => agg[d.key]).map((d) => {
              const a = agg[d.key];
              return (
                <div key={d.key} className="flex items-center gap-4">
                  <span className="text-sm text-[#9CA3AF] w-44 shrink-0">{d.name}</span>
                  <div className="flex-1 flex h-4 rounded-full overflow-hidden bg-[#0D0E12]">
                    {Object.entries(a.distribution).map(([bucket, cnt], bi) => (<div key={bucket} title={`${bucket}: ${cnt}`} style={{ width: `${(cnt / a.n) * 100}%`, backgroundColor: DIM_COLOR[d.key], opacity: 0.35 + bi * 0.2 }} />))}
                  </div>
                  <span className="font-mono text-xs text-[#6B7280] w-24 text-right">σ {a.variation}</span>
                </div>
              );
            })}
          </div>
          {saMode && (
            <div className="mt-6 pt-6 border-t border-[var(--cg-border)]" data-testid="orgwide-narrative">
              <ReportEditor interp={interp} onGenerate={generate} genBusy={busy} onSave={saveEdit} onPdf={downloadPdf} n={data.n} prefix="orgwide" />
            </div>
          )}
          <OrgCohortCompare orgId={orgId} cohorts={data?.cohorts} selKey={selIds.join(",")} />
        </>
      ) : null}
    </div>
  );
}

function OrgCohortCompare({ orgId, cohorts, selKey }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [resA, setResA] = useState(null);
  const [resB, setResB] = useState(null);
  const [note, setNote] = useState(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const fetchSide = useCallback((name, setter) => {
    if (!name) { setter(null); return; }
    const p = new URLSearchParams();
    p.set("cohort", name);
    if (selKey) p.set("campaign_ids", selKey);
    api.get(withOrgId(`/org/aggregate?${p.toString()}`, orgId)).then((r) => setter({ name, ...r.data })).catch(() => setter(null));
  }, [orgId, selKey]);
  useEffect(() => { fetchSide(a, setResA); }, [a, fetchSide]);
  useEffect(() => { fetchSide(b, setResB); }, [b, fetchSide]);
  useEffect(() => { setNote(null); }, [a, b, selKey]);
  const genNote = async () => {
    if (!a || !b) return;
    setNoteBusy(true);
    try {
      const p = new URLSearchParams();
      p.set("cohort_a", a); p.set("cohort_b", b);
      if (selKey) p.set("campaign_ids", selKey);
      const r = await api.post(withOrgId(`/org/cohort-compare/narrative?${p.toString()}`, orgId));
      setNote(r.data.note);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setNoteBusy(false); }
  };
  const exportCsv = async () => {
    if (!a || !b) return;
    const token = localStorage.getItem("cg_token");
    const p = new URLSearchParams();
    p.set("cohort_a", a); p.set("cohort_b", b);
    if (selKey) p.set("campaign_ids", selKey);
    const r = await fetch(withOrgId(`${API}/org/cohort-compare.csv?${p.toString()}`, orgId), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not export comparison"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href = url; el.download = "cohort-comparison.csv"; el.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    if (!a || !b) return;
    const token = localStorage.getItem("cg_token");
    const p = new URLSearchParams();
    p.set("cohort_a", a); p.set("cohort_b", b);
    if (selKey) p.set("campaign_ids", selKey);
    const r = await fetch(withOrgId(`${API}/org/cohort-compare.pdf?${p.toString()}`, orgId), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not export comparison PDF"); return; }
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href = url; el.download = "cohort-comparison.pdf"; el.click(); URL.revokeObjectURL(url);
  };
  const selCls = "bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#F3F4F6]";
  const bothShown = resA && resB && !resA.suppressed && !resB.suppressed;
  const overlay = bothShown
    ? (resA.dimensions || []).filter((d) => resA.aggregate[d.key] && resB.aggregate[d.key])
        .map((d) => ({ name: d.name.split(" ")[0], a: resA.aggregate[d.key].average, b: resB.aggregate[d.key].average }))
    : [];
  const col = (side, r) => {
    if (!r) return <p className="text-sm text-[#6B7280]">Select a cohort</p>;
    if (r.suppressed) return <div data-testid={`cohortcmp-${side}-suppressed`} className="text-center py-6"><Lock className="w-6 h-6 text-ghostslate mx-auto mb-2" /><p className="text-sm text-[#9CA3AF]">{r.name}: {r.n}/{r.threshold} — suppressed</p></div>;
    return (
      <div data-testid={`cohortcmp-${side}`}>
        <p className="cg-eyebrow mb-2">{r.name} · n={r.n}</p>
        {(r.dimensions || []).filter((d) => r.aggregate[d.key]).map((d) => (
          <div key={d.key} className="mb-2">
            <div className="flex justify-between text-xs text-[#9CA3AF] mb-0.5"><span>{d.name}</span><span className="font-mono">{r.aggregate[d.key].average}</span></div>
            <div className="h-2 rounded-full bg-[#0D0E12]"><div className="h-2 rounded-full" style={{ width: `${(r.aggregate[d.key].average / 5) * 100}%`, backgroundColor: DIM_COLOR[d.key] }} /></div>
          </div>
        ))}
      </div>
    );
  };
  if ((cohorts || []).length < 2) return null;
  return (
    <div className="mt-6 pt-6 border-t border-[var(--cg-border)]" data-testid="org-cohort-compare">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <p className="cg-eyebrow">Compare two teams / business units · privacy threshold enforced</p>
        <div className="flex gap-2">
          <button data-testid="cohortcmp-csv" onClick={exportCsv} disabled={!a || !b} className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40"><Download className="w-4 h-4" /> CSV</button>
          <button data-testid="cohortcmp-pdf" onClick={exportPdf} disabled={!a || !b} className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40"><FileText className="w-4 h-4" /> PDF</button>
        </div>
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <select data-testid="cohortcmp-a" className={selCls} value={a} onChange={(e) => setA(e.target.value)}><option value="">Cohort A</option>{cohorts.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select data-testid="cohortcmp-b" className={selCls} value={b} onChange={(e) => setB(e.target.value)}><option value="">Cohort B</option>{cohorts.map((c) => <option key={c} value={c}>{c}</option>)}</select>
      </div>
      {bothShown && overlay.length > 0 && (
        <div data-testid="cohortcmp-overlay" className="mb-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={overlay} margin={{ left: -15 }} barGap={2}>
              <XAxis dataKey="name" stroke="#6B7280" fontSize={10} interval={0} angle={-20} textAnchor="end" height={54} />
              <YAxis domain={[0, 5]} stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1A1D27", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="a" name={resA.name} fill="#D9A25F" radius={[4, 4, 0, 0]} />
              <Bar dataKey="b" name={resB.name} fill="#6B8CBE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {bothShown && overlay.length > 0 && (() => {
        const gaps = overlay.map((o) => ({ name: o.name, gap: +(o.a - o.b).toFixed(2) }));
        const biggest = gaps.reduce((m, g) => (Math.abs(g.gap) > Math.abs(m.gap) ? g : m), gaps[0]);
        return (
          <div className="mb-6" data-testid="cohortcmp-deltas">
            <p className="cg-eyebrow mb-2">Gap per dimension · {resA.name} − {resB.name}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {gaps.map((g) => <span key={g.name} className="text-xs font-mono px-2 py-1 rounded-full bg-[#0D0E12] border border-[var(--cg-border)]" style={{ color: g.gap >= 0 ? "#D9A25F" : "#6B8CBE" }}>{g.name} {g.gap >= 0 ? "+" : ""}{g.gap}</span>)}
            </div>
            <p className="text-sm text-[#E8EAEE]" data-testid="cohortcmp-biggest">Largest difference: <span className="text-brass">{biggest.name}</span> — {Math.abs(biggest.gap)} higher for {biggest.gap >= 0 ? resA.name : resB.name}.</p>
          </div>
        );
      })()}
      {!orgId && bothShown && overlay.length > 0 && (
        <div className="mb-6" data-testid="cohortcmp-note-wrap">
          <button data-testid="cohortcmp-note-btn" onClick={genNote} disabled={noteBusy} className="inline-flex items-center gap-2 border border-[var(--cg-border)] text-sm text-[#9CA3AF] px-4 py-2 rounded-full cg-scale-btn hover:border-brass disabled:opacity-40"><Sparkles className="w-4 h-4" /> {noteBusy ? "Generating…" : "Generate AI note on the gaps"}</button>
          {note && (
            <div className="mt-3 bg-[#0D0E12] border border-[var(--cg-border)] rounded-xl p-4" data-testid="cohortcmp-note">
              <p className="cg-eyebrow mb-1">AI note · aggregate only · cautious</p>
              <p className="text-[#D6D9DF] leading-relaxed text-[0.95rem] whitespace-pre-line">{note}</p>
            </div>
          )}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">{col("cohort_a", resA)}{col("cohort_b", resB)}</div>
    </div>
  );
}

function CampaignAdmin({ orgId, orgName, onChanged }) {
  const [list, setList] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [modal, setModal] = useState(null); // { check, type }
  const [confirmText, setConfirmText] = useState("");
  const [understand, setUnderstand] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/organizations/${orgId}/campaigns?include_archived=${includeArchived}`);
      setList(r.data.campaigns);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [orgId, includeArchived]);
  useEffect(() => { load(); }, [load]);

  const openDelete = async (c) => {
    try {
      const r = await api.get(`/admin/campaigns/${c.id}/deletion-check`);
      setConfirmText(""); setUnderstand(false);
      setModal({ ...r.data, type: r.data.classification });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const close = () => { setModal(null); setConfirmText(""); setUnderstand(false); };

  const doArchive = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/campaigns/${id}/archive`); toast.success("Campaign archived"); close(); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doRestore = async (id) => {
    try { await api.post(`/admin/campaigns/${id}/restore`); toast.success("Campaign restored"); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDeleteEmpty = async () => {
    setBusy(true);
    try { await api.delete(`/admin/campaigns/${modal.campaign.id}`, { data: { confirm: confirmText } }); toast.success("Campaign deleted"); close(); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doPurgeTest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/campaigns/${modal.campaign.id}/purge-test`, { campaign_name: confirmText, understand });
      toast.success(`Test campaign deleted (${Object.values(r.data.deleted_counts).reduce((a, b) => a + b, 0)} records)`);
      close(); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const badge = (c) => c.archived
    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#222634] text-[#9CA3AF]">archived</span>
    : (c.is_test_demo ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(217,162,95,0.15)] text-brass">test/demo</span>
      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(125,157,134,0.15)] text-[#7D9D86]">active</span>);

  return (
    <div className="cg-card p-5 mt-6" data-testid="campaign-admin">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-lg text-[#F3F4F6] flex items-center gap-2"><Building2 className="w-4 h-4 text-brass" /> Manage campaigns <span className="text-xs text-[#6B7280] font-sans">(Super Admin)</span></h3>
        <label className="flex items-center gap-2 text-xs text-[#9CA3AF] cursor-pointer">
          <input type="checkbox" data-testid="campaign-admin-archived-toggle" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="w-4 h-4 accent-[#D9A25F]" /> Show archived
        </label>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-[#6B7280]">No campaigns.</p>}
        {list.map((c) => (
          <div key={c.id} data-testid={`campaign-admin-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-[var(--cg-border)] rounded-lg px-4 py-3 bg-[#0D0E12]">
            <div className="min-w-0">
              <p className="text-sm text-[#E8EAEE] flex items-center gap-2">{c.name} {badge(c)}</p>
              <p className="text-xs text-[#6B7280] font-mono mt-0.5">{c.archived ? (c.archived_code || c.code) : c.code} · {c.counts.completed} completed · {c.counts.in_progress} in-progress</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {c.archived ? (
                <button data-testid={`campaign-restore-${c.id}`} onClick={() => doRestore(c.id)} className="text-xs px-3 py-1 rounded-full bg-[#222634] text-[#9CA3AF] hover:text-brass cg-scale-btn inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Restore</button>
              ) : (
                <button data-testid={`campaign-archive-${c.id}`} onClick={() => doArchive(c.id)} className="text-xs px-3 py-1 rounded-full bg-[#222634] text-[#9CA3AF] hover:text-brass cg-scale-btn inline-flex items-center gap-1"><Archive className="w-3 h-3" /> Archive</button>
              )}
              <button data-testid={`campaign-delete-${c.id}`} onClick={() => openDelete(c)} className="text-xs px-3 py-1 rounded-full bg-[rgba(196,98,67,0.15)] text-terracotta hover:bg-[rgba(196,98,67,0.3)] cg-scale-btn inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="campaign-delete-modal" onClick={close}>
          <div className="cg-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            {modal.type === "EMPTY" && (
              <>
                <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Delete this campaign?</h3>
                <p className="text-sm text-[#9CA3AF] mb-4">You are about to permanently delete <span className="text-[#F3F4F6]">{modal.campaign.name}</span> from <span className="text-[#F3F4F6]">{modal.campaign.organization_name}</span>. This action cannot be undone.</p>
                <label className="cg-eyebrow block mb-1">Type DELETE to confirm</label>
                <input data-testid="campaign-delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] font-mono mb-4" placeholder="DELETE" />
                <div className="flex gap-2 justify-end">
                  <button className="text-sm text-[#9CA3AF] px-4 py-2 rounded-full border border-[var(--cg-border)]" onClick={close}>Cancel</button>
                  <button data-testid="campaign-delete-confirm-button" disabled={confirmText.trim() !== "DELETE" || busy} onClick={doDeleteEmpty} className="text-sm bg-terracotta text-white px-5 py-2 rounded-full disabled:opacity-40">Delete Campaign</button>
                </div>
              </>
            )}
            {modal.type === "HAS_DATA" && (
              <>
                <h3 className="font-serif text-xl text-[#F3F4F6] mb-3">Cannot delete this campaign</h3>
                <p className="text-sm text-[#9CA3AF] mb-5">This campaign contains assessment data and cannot be permanently deleted from this screen because doing so could affect historical assessment and research records.</p>
                <div className="flex gap-2 justify-end">
                  <button className="text-sm text-[#9CA3AF] px-4 py-2 rounded-full border border-[var(--cg-border)]" onClick={close}>Cancel</button>
                  <button data-testid="campaign-archive-instead-button" disabled={busy} onClick={() => doArchive(modal.campaign.id)} className="text-sm bg-[#222634] text-brass px-5 py-2 rounded-full inline-flex items-center gap-1"><Archive className="w-4 h-4" /> Archive Campaign</button>
                </div>
              </>
            )}
            {modal.type === "TEST_DEMO" && (
              <>
                <h3 className="font-serif text-xl text-terracotta mb-3">Permanently delete test campaign</h3>
                <p className="text-sm text-[#9CA3AF] mb-3">This TEST/DEMO campaign <span className="text-[#F3F4F6]">{modal.campaign.name}</span> contains data. This will permanently delete the records that belong exclusively to this campaign:</p>
                <ul className="text-xs text-[#9CA3AF] font-mono bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3 mb-4 space-y-0.5">
                  <li>assessment sessions: {modal.counts.sessions_total}</li>
                  <li>calculated scores: {modal.counts.scores}</li>
                  <li>interpretations: {modal.counts.interpretations}</li>
                  <li>memberships: {modal.counts.memberships}</li>
                </ul>
                <p className="text-xs text-[#6B7280] mb-3">User accounts, independent assessments, payments and other organizations are never affected.</p>
                <label className="cg-eyebrow block mb-1">Type the exact campaign name to confirm</label>
                <input data-testid="campaign-purge-name-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] font-mono mb-3" placeholder={modal.campaign.name} />
                <label className="flex items-start gap-2 text-sm text-[#9CA3AF] mb-4 cursor-pointer">
                  <input type="checkbox" data-testid="campaign-purge-understand" checked={understand} onChange={(e) => setUnderstand(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C46243]" />
                  I understand that this will permanently delete test/demo records associated exclusively with this campaign.
                </label>
                <div className="flex gap-2 justify-end">
                  <button className="text-sm text-[#9CA3AF] px-4 py-2 rounded-full border border-[var(--cg-border)]" onClick={close}>Cancel</button>
                  <button data-testid="campaign-purge-confirm-button" disabled={confirmText.trim() !== modal.campaign.name || !understand || busy} onClick={doPurgeTest} className="text-sm bg-terracotta text-white px-5 py-2 rounded-full disabled:opacity-40">Permanently Delete Test Campaign</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
